/**
 * Small "Make referrer" button. Opens a dialog with type + commission plan.
 * Idempotent: if the user is already a referrer, the API returns the existing
 * one and we just show a link to the CRM page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function MakeReferrerButton({ user, size = "sm", variant = "outline", onDone }) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [structures, setStructures] = useState([]);
  const [form, setForm] = useState({ type_code: "agent", commission_structure_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/masters/lookup/referrer_type").then((r) => setTypes(r.data || []));
    api.get("/masters/lookup/commission_structure").then((r) => setStructures(r.data || []));
  }, [open]);

  const filteredStructures = useMemo(() => {
    return structures.filter((s) => (s.data?.referrer_type_code || "") === form.type_code);
  }, [structures, form.type_code]);

  useEffect(() => {
    if (filteredStructures.length && !filteredStructures.find((s) => s.id === form.commission_structure_id)) {
      setForm((f) => ({ ...f, commission_structure_id: filteredStructures[0].id }));
    }
  }, [filteredStructures, form.commission_structure_id]);

  const submit = async () => {
    setErr(null); setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/invite-referrer`, {
        user_id: user.id,
        type_code: form.type_code,
        commission_structure_id: form.commission_structure_id,
      });
      if (data.existing) {
        setExisting(data.referrer);
      } else {
        toast.success(`Invite sent · code ${data.referrer.code}`);
        setOpen(false);
        onDone?.(data.referrer);
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        data-testid={`make-referrer-${user.id}`}
        className="text-xs"
      >
        <Coins className="w-3.5 h-3.5 mr-1.5" /> Make referrer
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-2xl">Invite as referrer</DialogTitle>
            <DialogDescription>Turn <strong>{user.name}</strong> into a partner. We'll email them their share code + terms.</DialogDescription>
          </DialogHeader>

          {existing ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-primary/10 text-primary text-sm">
                <div className="font-medium">Already a referrer</div>
                <div className="mt-1 font-mono">{existing.code}</div>
              </div>
              <Link to="/app/crm/referrers"><Button variant="outline" size="sm" className="w-full">View in CRM</Button></Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v, commission_structure_id: "" })}>
                  <SelectTrigger className="mt-1.5" data-testid="mkref-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.data?.code || t.label.toLowerCase()}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Commission plan</Label>
                <Select value={form.commission_structure_id} onValueChange={(v) => setForm({ ...form, commission_structure_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="mkref-structure">
                    <SelectValue placeholder={filteredStructures.length ? "Choose plan" : "No plans for this type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStructures.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
            </div>
          )}

          {!existing && (
            <DialogFooter>
              <Button onClick={submit} disabled={saving || !form.commission_structure_id} data-testid="mkref-submit">
                {saving ? "Sending invite…" : <><UserPlus className="w-4 h-4 mr-2" />Send invite + create</>}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
